import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { message } from 'antd'
import { HiOutlineEye, HiOutlineEyeSlash, HiMiniXCircle, HiCheckCircle } from 'react-icons/hi2'
import ReCAPTCHA from 'react-google-recaptcha'

import './styles.scss'
import uris from '../../../config/uris'
import { encrypt_forge } from '../../../helpers/encrypt/forge-encrypt'
import { ResetPasswordAction, ResetPasswordReset } from '../../../store/slices/auth/resetPassword'

const ResetPassword = () => {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    })
    const [showPassword, setShowPassword] = useState(false)
    const [isPasswordFieldTouched, setIsPasswordFieldTouched] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [formErrors, setFormErrors] = useState({
        passwordConditions: [false, false, false, false, false, false],
        isPasswordValid: false,
        confirmPassword: '',
        isConfirmPasswordMatched: null,
    })
    const [isCaptchaChecked, setIsCaptchaChecked] = useState(false)
    const [captchaError, setCaptchaError] = useState('')

    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { data: resetPasswordData, isLoading, error: resetPasswordError, success: resetPasswordSuccess } = useSelector((state) => state.resetPassword)
    const isDevPlatform = process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'local';

    const errorMessages = [
        'Password should contain at least 8-25 characters',
        'Password should contain at least one uppercase letter [A-Z]',
        'Password should contain at least one lowercase letter [a-z]',
        'Password should contain at least one number [0-9]',
        'Password should contain at least one special character [! @ # $ %]',
        'Password should not contain any other characters',
    ]

    const validatePassword = (password) => {
        const conditions = [
            password.length >= 8 && password.length <= 25,
            /[A-Z]/.test(password),
            /[a-z]/.test(password),
            /\d/.test(password),
            /[!@#$%]/.test(password),
            /^[A-Za-z0-9!@#$%]+$/.test(password),
        ]
        return conditions
    }

    // Helper function to handle confirm password validation
    const updateConfirmPasswordError = (password, confirmPassword) => {
        const isConfirmPasswordMatched = confirmPassword && password === confirmPassword
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            confirmPassword: confirmPassword ? (isConfirmPasswordMatched ? 'Password matched' : 'Passwords do not match') : 'Password field cannot be empty',
            isConfirmPasswordMatched,
        }))
    }

    const handleInputChange = (field, value) => {
        setFormData((prevData) => ({ ...prevData, [field]: value }))

        if (field === 'password') {
            setIsPasswordFieldTouched(value.length > 0)

            const passwordConditions = validatePassword(value)
            const isPasswordValid = passwordConditions.every(Boolean)

            setFormErrors((prevErrors) => ({
                ...prevErrors,
                passwordConditions,
                isPasswordValid,
            }))

            if (formData.confirmPassword) {
                updateConfirmPasswordError(value, formData.confirmPassword)
            }
            return
        }

        if (field === 'confirmPassword') {
            updateConfirmPasswordError(formData.password, value)
            return
        }
    }

    const handleCaptchaChange = () => {
        setIsCaptchaChecked(true)
        setCaptchaError('')
    }

    const handleSubmit = () => {
        if (!formErrors.isPasswordValid) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: 'Enter a valid password',
                isConfirmPasswordMatched: false,
            }))
            return
        }

        if (!isCaptchaChecked) {
            setCaptchaError('*Please verify that you are not a robot.')
            return
        }

        if (formData.password === formData.confirmPassword) {
            const token = location.pathname.split('/activate/')[1]
            const payload = { password: encrypt_forge(formData.password) }

            dispatch(ResetPasswordAction({ token, payload }))
        } else {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: 'Passwords does not match',
            }))
        }
    }

    const toggleVisibility = (field) => {
        if (field === 'password') {
            setShowPassword((prev) => !prev)
        } else {
            setShowConfirmPassword((prev) => !prev)
        }
    }

    useEffect(() => {
        if (resetPasswordSuccess && resetPasswordData?.message) {
            message.success(resetPasswordData.message)
            dispatch(ResetPasswordReset())
            navigate(uris.login)
        }
        if (resetPasswordError?.message) {
            message.error(resetPasswordError.message)
            dispatch(ResetPasswordReset())
        }
    }, [resetPasswordSuccess, resetPasswordError])

    useEffect(() => {
        if (isDevPlatform) {
            setIsCaptchaChecked(true);
        }
    }, [isDevPlatform])

    return (
        <div className="reset-password-page">
            <div className="reset-password-form-container">
                <div className="form-contents">
                    <div className="card-title">Reset Password</div>

                    <div className="password-section">
                        <div className="label">New Password</div>
                        <div className="input-area">
                            <div className="overlap-group">
                                <input
                                    className="form-input"
                                    name="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter New Password"
                                    onChange={(e) => {
                                        if (!isPasswordFieldTouched) setIsPasswordFieldTouched(true)

                                        handleInputChange('password', e.target.value)
                                    }}
                                    value={formData.password}
                                />
                                <div className="password-hide-icon" onClick={() => toggleVisibility('password')}>
                                    {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                                </div>
                            </div>
                        </div>
                        {isPasswordFieldTouched && (
                            <div className="initial-error-message">
                                {errorMessages.map((error, index) => (
                                    <div key={index} className={`error-item ${formErrors.passwordConditions[index] ? 'success-icon' : 'error-icon'}`}>
                                        {formErrors.passwordConditions[index] ? (
                                            <span>
                                                <HiCheckCircle /> {error}{' '}
                                            </span>
                                        ) : (
                                            <span>
                                                {' '}
                                                <HiMiniXCircle /> {error}{' '}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="password-section confirm-password">
                        <div className="label">Confirm Password</div>
                        <div className="input-area">
                            <div className="overlap-group">
                                <input
                                    className="form-input"
                                    name="confirm-password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Enter Confirm Password"
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    value={formData.confirmPassword}
                                />
                                <div className="password-hide-icon" onClick={() => toggleVisibility('confirmPassword')}>
                                    {showConfirmPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                                </div>
                            </div>
                        </div>

                        {formErrors.confirmPassword && (
                            <div className={`confirm-password-message ${formErrors.isConfirmPasswordMatched ? 'success-icon' : 'error-icon'}`}>
                                {formErrors.isConfirmPasswordMatched ? (
                                    <>
                                        <HiCheckCircle /> <span>{formErrors.confirmPassword}</span>
                                    </>
                                ) : (
                                    <>
                                        {' '}
                                        <HiMiniXCircle /> <span>{formErrors.confirmPassword}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {process.env.REACT_APP_ENVIRONMENT === "production" && (<div className="recaptcha-section">
                        <ReCAPTCHA
                            name="verified"
                            className={`recaptcha ${isCaptchaChecked ? 'red-class' : ''}`}
                            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
                            onChange={handleCaptchaChange}
                            size="normal"
                            theme="light"
                        />
                        {captchaError && <div className="error-message">{captchaError}</div>}
                    </div>)}

                    <div className="form-button" onClick={handleSubmit}>
                        <div className="text-wrapper">{isLoading ? 'Updating...' : 'Update'}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ResetPassword
